import React, { useEffect, useState } from "react";
import {
  postCustomerFormKiosk,
  listDailyPrices,
  isErpApiSuccess,
} from "../erpClient";
import toast from "react-hot-toast";
import CustHeader from "./CustHeader";
import { Modal } from "react-bootstrap";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import {
  sanitizeMobileInput,
  mobileInputFieldProps,
  getMobileValidationError,
} from "../utils/mobileInput";
import { useMobileExistsCheck } from "../utils/useMobileExistsCheck";
import MobileExistsFeedback from "../components/MobileExistsFeedback";

const REFERENCE_OPTIONS = [
  "Relative/Friends",
  "Social media",
  "Youtube",
  "Holdings",
  "Autorickshaw ad",
  "Newspaper/Template",
];

const OPTIONAL_FIELDS = [
  { key: "email", label: "Email", type: "email" },
  { key: "state", label: "State", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "address", label: "Address", type: "text" },
  { key: "aadhar_no", label: "Aadhar No", type: "text" },
  { key: "pan_no", label: "PAN No", type: "text" },
  { key: "birth_date", label: "Birth Date", type: "date" },
  { key: "marital_status", label: "Marital Status", type: "text" },
  { key: "anniversary_date", label: "Anniversary Date", type: "date" },
  { key: "company_name", label: "Company Name", type: "text" },
  { key: "company_address", label: "Company Address", type: "text" },
  { key: "GST_number", label: "GST Number", type: "text" },
  { key: "area", label: "Area", type: "text" },
];

function CustomerForm() {
  const { buttonClass } = useButtonTableStyle();
  const [fullName, setFullName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const mobileExistsCheck = useMobileExistsCheck(mobileNo);
  const [purposeOfVisit, setPurposeOfVisit] = useState("");
  const [noOfVisitors, setNoOfVisitors] = useState(0);
  const [functionPurpose, setFunctionPurpose] = useState("");
  const [functionDate, setFunctionDate] = useState("");
  const [scheme, setScheme] = useState("No");
  const [instagramFollow, setInstagramFollow] = useState("No");
  const [reference, setReference] = useState("");
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [optionalData, setOptionalData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [successToken, setSuccessToken] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [masterGoldRate, setMasterGoldRate] = useState(null);
  const [masterSilverRate, setMasterSilverRate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listDailyPrices();
        if (cancelled || !isErpApiSuccess(res)) return;
        const rows = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
        const latest = rows[0];
        if (!latest) return;
        const gold24 =
          latest["24_carat_gold"] ?? latest.goldRate ?? latest.gold_rate;
        const silver = latest.silver ?? latest.silver_rate;
        if (gold24 != null && gold24 !== "") setMasterGoldRate(Number(gold24));
        if (silver != null && silver !== "") setMasterSilverRate(Number(silver));
      } catch {
        // optional display — ignore failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resetForm = () => {
    setFullName("");
    setMobileNo("");
    setPurposeOfVisit("");
    setNoOfVisitors(0);
    setFunctionPurpose("");
    setFunctionDate("");
    setScheme("No");
    setInstagramFollow("No");
    setReference("");
    setOptionalData({});
    setShowOptionalFields(false);
  };

  const handleOptionalChange = (key, value) => {
    setOptionalData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    const mobileError = getMobileValidationError(mobileNo, { required: true });
    if (mobileError) {
      toast.error(mobileError);
      return;
    }

    if (mobileExistsCheck.exists) {
      toast(
        "This mobile is already registered — we will link to your existing profile.",
        { icon: "⚠️" },
      );
    }

    const requestData = {
      type: "create",
      name: fullName.trim(),
      mobile: mobileNo,
      purpose_of_visit: purposeOfVisit,
      no_of_visitors: Number(noOfVisitors) || 0,
      function_purpose: functionPurpose,
      function_date: functionDate || undefined,
      scheme,
      instagram: instagramFollow,
      about: reference,
      branch_id: 1,
      ...Object.fromEntries(
        Object.entries(optionalData).filter(
          ([, v]) => v != null && String(v).trim() !== "",
        ),
      ),
      ...(reference ? { reference } : {}),
    };

    try {
      setSubmitting(true);
      const data = await postCustomerFormKiosk(requestData);

      if (!isErpApiSuccess(data)) {
        toast.error(data?.message || "Failed to submit form");
        return;
      }

      const token =
        data?.data?.token_number ?? data?.token_number ?? data?.data?.token ?? "";
      setSuccessToken(token);
      setShowSuccessModal(true);
      if (token) {
        toast.success(`Your token: ${token}`);
      } else {
        toast.success("Registration successful!");
      }

      resetForm();
      setTimeout(() => setShowSuccessModal(false), 4000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to submit form",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <CustHeader />
      <div
        className="container d-flex justify-content-center align-items-center min-vh-100"
        style={{ marginTop: "80px" }}
      >
        <div className="col-lg-6">
          <div className="card shadow-lg">
            <div className="card-header bg-primary text-white text-center">
              <h4 className="mb-0">Customer Walk-in Registration</h4>
              {(masterGoldRate != null || masterSilverRate != null) && (
                <small className="d-block mt-1 opacity-75">
                  {masterGoldRate != null && (
                    <span className="me-3">
                      Gold 24K: ₹{masterGoldRate.toLocaleString("en-IN")}
                    </span>
                  )}
                  {masterSilverRate != null && (
                    <span>Silver: ₹{masterSilverRate.toLocaleString("en-IN")}</span>
                  )}
                </small>
              )}
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="fullName" className="form-label">
                    Full Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${buttonClass}`}
                    id="fullName"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="mobileNo" className="form-label">
                    Mobile No <span className="text-danger">*</span>
                  </label>
                  <input
                    {...mobileInputFieldProps}
                    className={`form-control ${buttonClass} ${
                      mobileExistsCheck.exists ? "border-warning" : ""
                    }`}
                    id="mobileNo"
                    pattern="[0-9]{10}"
                    placeholder="e.g. 9123456790"
                    value={mobileNo}
                    onChange={(e) =>
                      setMobileNo(sanitizeMobileInput(e.target.value))
                    }
                    required
                  />
                  <MobileExistsFeedback
                    exists={mobileExistsCheck.exists}
                    checking={mobileExistsCheck.checking}
                    message="Mobile already registered — you can still continue"
                    className={mobileExistsCheck.exists ? "text-warning" : ""}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="purposeOfVisit" className="form-label">
                    Purpose of Visit
                  </label>
                  <input
                    type="text"
                    className={`form-control ${buttonClass}`}
                    id="purposeOfVisit"
                    placeholder="e.g. looking for gold chain"
                    value={purposeOfVisit}
                    onChange={(e) => setPurposeOfVisit(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="noOfVisitors" className="form-label">
                    No of Visitors
                  </label>
                  <input
                    type="number"
                    className={`form-control ${buttonClass}`}
                    id="noOfVisitors"
                    value={noOfVisitors}
                    onChange={(e) => setNoOfVisitors(e.target.value)}
                    min="0"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="functionPurpose" className="form-label">
                    Function Purpose
                  </label>
                  <input
                    type="text"
                    className={`form-control ${buttonClass}`}
                    id="functionPurpose"
                    placeholder="e.g. Wedding"
                    value={functionPurpose}
                    onChange={(e) => setFunctionPurpose(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="functionDate" className="form-label">
                    Function Date
                  </label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    id="functionDate"
                    value={functionDate}
                    onChange={(e) => setFunctionDate(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Do you know about the scheme?
                  </label>
                  <div className="d-flex gap-3">
                    {["Yes", "No"].map((val) => (
                      <div className="form-check" key={val}>
                        <input
                          type="radio"
                          id={`scheme${val}`}
                          name="scheme"
                          value={val}
                          checked={scheme === val}
                          onChange={(e) => setScheme(e.target.value)}
                          className="form-check-input"
                        />
                        <label htmlFor={`scheme${val}`} className="form-check-label">
                          {val}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Do you follow us on Instagram?
                  </label>
                  <div className="d-flex gap-3">
                    {["Yes", "No"].map((val) => (
                      <div className="form-check" key={val}>
                        <input
                          type="radio"
                          id={`instagram${val}`}
                          name="instagramFollow"
                          value={val}
                          checked={instagramFollow === val}
                          onChange={(e) => setInstagramFollow(e.target.value)}
                          className="form-check-input"
                        />
                        <label
                          htmlFor={`instagram${val}`}
                          className="form-check-label"
                        >
                          {val}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">How did you hear about us?</label>
                  <div className="d-flex flex-column">
                    {REFERENCE_OPTIONS.map((opt) => (
                      <div className="form-check mb-2" key={opt}>
                        <input
                          type="radio"
                          id={`ref-${opt}`}
                          name="reference"
                          value={opt}
                          checked={reference === opt}
                          onChange={(e) => setReference(e.target.value)}
                          className="form-check-input"
                        />
                        <label htmlFor={`ref-${opt}`} className="form-check-label">
                          {opt}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <button
                    type="button"
                    className={`btn btn-link p-0 ${buttonClass}`}
                    onClick={() => setShowOptionalFields((v) => !v)}
                  >
                    {showOptionalFields ? "Hide" : "Show"} additional details
                  </button>
                  {showOptionalFields && (
                    <div className="mt-2 border rounded p-3">
                      {OPTIONAL_FIELDS.map(({ key, label, type }) => (
                        <div className="mb-2" key={key}>
                          <label className="form-label small">{label}</label>
                          <input
                            type={type}
                            className={`form-control form-control-sm ${buttonClass}`}
                            value={optionalData[key] || ""}
                            onChange={(e) => handleOptionalChange(key, e.target.value)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className={`btn btn-primary ${buttonClass}`}
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
      >
        <Modal.Body className="text-center py-4">
          <h5 className="text-success mb-3">Thank you!</h5>
          {successToken ? (
            <>
              <p className="mb-2">Your token number is:</p>
              <span
                className="badge bg-primary fs-4 px-4 py-2"
                style={{ letterSpacing: "0.05em" }}
              >
                {successToken}
              </span>
              <p className="text-muted small mt-3 mb-0">
                Please wait — our team will call your token shortly.
              </p>
            </>
          ) : (
            <p className="mb-0">Registration successful. Please wait for your token.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CustomerForm;
